import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaUserPlus } from 'react-icons/fa';
import { db } from '../config/Firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

// Animation variants with black outlines
const containerVariants = {
  hidden: { 
    opacity: 0, 
    y: 20, 
    filter: 'grayscale(100%) brightness(0.8)',
  },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'grayscale(0%) brightness(1)',
    transition: { 
      duration: 0.8,
      ease: [0.33, 1, 0.68, 1],
      filter: { duration: 1.5 }
    },
  },
};

const buttonVariants = {
  hover: { 
    scale: 1.05,
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    filter: 'contrast(120%) brightness(1.1)',
    transition: { duration: 0.3 }
  },
  tap: { 
    scale: 0.97,
    filter: 'contrast(90%) brightness(0.95)'
  },
};

const cardVariants = {
  hidden: { 
    opacity: 0, 
    y: 15,
    filter: 'grayscale(100%) brightness(0.9)',
    scale: 0.98
  },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    filter: 'grayscale(0%) brightness(1)',
    scale: 1,
    transition: { 
      duration: 0.5,
      ease: [0.33, 1, 0.68, 1],
      delay: i * 0.08,
      filter: { duration: 0.8 }
    },
  }),
  hover: {
    scale: 1.03,
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
    filter: 'contrast(105%) brightness(1.02)',
    transition: { duration: 0.3 }
  }
};

const VisitorSearch = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      if (!searchQuery.trim()) {
        setResults([]);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const visitorsRef = collection(db, 'Visitors');
        const lowerQuery = searchQuery.toLowerCase();
        const upperQuery = lowerQuery + '\uf8ff';

        const queries = [
          query(visitorsRef, where('fullName', '>=', lowerQuery), where('fullName', '<=', upperQuery)),
          query(visitorsRef, where('email', '>=', lowerQuery), where('email', '<=', upperQuery)),
          query(visitorsRef, where('contact', '>=', lowerQuery), where('contact', '<=', upperQuery)),
        ];

        const resultsSet = new Set();
        for (const q of queries) {
          const snapshot = await getDocs(q);
          snapshot.forEach((doc) => {
            resultsSet.add({ id: doc.id, ...doc.data() });
          });
        }

        setResults(Array.from(resultsSet));
      } catch (err) {
        setError('Failed to fetch visitors. Please try again.');
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  return (
    <motion.div
      className="w-full max-w-4xl mx-auto mt-10 px-4"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Search Header with Black Outlines */}
      <motion.div 
        className="flex flex-col sm:flex-row justify-between items-center bg-white p-6 rounded-xl shadow-2xl border-4 border-black" // Thicker black border
        whileHover={{ scale: 1.005 }}
        transition={{ duration: 0.3 }}
        style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
      >
        <div className="relative w-full sm:w-auto mb-4 sm:mb-0">
          <motion.div
            className="absolute left-3 top-1/2 transform -translate-y-1/2"
            animate={{
              color: isFocused ? '#000' : '#4b5563',
              scale: isFocused ? 1.1 : 1
            }}
            transition={{ duration: 0.2 }}
          >
            <FaSearch />
          </motion.div>
          <motion.input
            type="text"
            placeholder="Search by name, email, or phone..."
            className="w-full sm:w-96 pl-10 pr-4 py-3 rounded-lg bg-gray-50 border-2 border-black text-gray-900 focus:outline-none focus:ring-2 focus:ring-black focus:border-black transition-all duration-300" // Black borders
            aria-label="Search visitors"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
          />
        </div>

        <motion.button
          className="flex items-center gap-2 bg-black hover:bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 transition-colors duration-300 border-2 border-black" // Black border
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          aria-label="Add new visitor"
          onClick={() => navigate('/NewVisitor')}
        >
          <FaUserPlus />
          New Visitor
        </motion.button>
      </motion.div>

      {/* Results Section with Black Outlines */}
      <motion.div 
        className="mt-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        {loading && (
          <motion.div 
            className="flex justify-center items-center py-8"
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: 1,
              transition: { 
                repeat: Infinity, 
                repeatType: 'reverse',
                duration: 1.8 
              }
            }}
          >
            <div className="w-12 h-12 border-4 border-gray-300 border-t-black rounded-full animate-spin"></div> {/* Black spinner */}
          </motion.div>
        )}

        {error && (
          <motion.div 
            className="bg-gray-100 text-gray-900 p-4 rounded-xl border-l-4 border-black shadow-sm mb-6" // Black border
            initial={{ x: -30, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            <p className="font-medium">{error}</p>
          </motion.div>
        )}

        {searchQuery && !loading && results.length === 0 && (
          <motion.div 
            className="text-center py-12 border-2 border-black rounded-xl bg-white p-6" // Black border
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-gray-600 text-lg">No matching visitors found</p>
            <p className="text-gray-500 mt-2">Try a different search term</p>
          </motion.div>
        )}

        {results.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {results.map((visitor, index) => (
              <motion.div
                key={visitor.id}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                animate="visible"
                whileHover="hover"
                className="bg-white p-5 rounded-xl shadow-md border-2 border-black cursor-pointer hover:shadow-lg transition-all duration-300" // Black border
                onClick={() => navigate(`/VisitorDetails/${visitor.id}`)}
              >
                <div className="flex flex-col items-center text-center">
                  {visitor.imageUrl ? (
                    <motion.div
                      className="relative mb-4"
                      whileHover={{ scale: 1.05 }}
                    >
                      <motion.img
                        src={visitor.imageUrl}
                        alt={`${visitor.fullName}'s photo`}
                        className="w-20 h-20 object-cover rounded-full border-2 border-black shadow-sm" // Black border
                        initial={{ filter: 'grayscale(100%) brightness(0.9)' }}
                        animate={{ filter: 'grayscale(20%) brightness(1)' }}
                        transition={{ duration: 0.8 }}
                      />
                      <motion.div 
                        className="absolute inset-0 rounded-full border-2 border-transparent"
                        whileHover={{ borderColor: 'rgba(0,0,0,0.2)' }}
                      />
                    </motion.div>
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center mb-4 border-2 border-black"> {/* Black border */}
                      <span className="text-2xl text-gray-600">
                        {visitor.fullName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-1">
                    {visitor.fullName}
                  </h3>
                  <p className="text-gray-700 text-sm mb-2">
                    {visitor.email}
                  </p>
                  <p className="text-gray-600 text-sm">
                    {visitor.contact}
                  </p>
                  
                  <motion.div 
                    className="mt-4 pt-3 w-full border-t border-black" // Black border
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                  >
                    <span className="text-xs text-gray-500">
                      Click for details
                    </span>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default VisitorSearch;